import { createRouter } from "@tanstack/solid-router";
import { NotFoundRoute } from "@/components/not-found-route";
import { rootRoute } from "@/routes/__root";
import { authRoute } from "@/routes/_auth/route";
import { loginRoute } from "@/routes/_auth/login";
import { registerRoute } from "@/routes/_auth/register";
import { forgotPasswordRoute } from "@/routes/_auth/forgot-password";
import { resetPasswordRoute } from "@/routes/_auth/reset-password";
import { verifyEmailRoute } from "@/routes/_auth/verify-email";
import { socialCallbackRoute } from "@/routes/_auth/social-callback";
import { appRoute } from "@/routes/_app/route";
import { indexRoute } from "@/routes/_app/index";
import { catalogRoute } from "@/routes/_app/catalog";
import { catalogProductRoute } from "@/routes/_app/catalog.$productId";
import { scannerRoute } from "@/routes/_app/scanner";
import { collectionRoute } from "@/routes/_app/collection";
import { marketRoute } from "@/routes/_app/market";
import { journalRoute } from "@/routes/_app/journal";
import { journalPrivacyRoute } from "@/routes/_app/journal.privacy";
import { ranksRoute } from "@/routes/_app/ranks";
import { stickersRoute } from "@/routes/_app/stickers";
import { communityRoute } from "@/routes/_app/community";
// 👥 Wave 1 · agente D — grafo social + chat/DM + notificaciones
import { socialFollowersRoute } from "@/routes/_app/social.followers";
import { socialFollowingRoute } from "@/routes/_app/social.following";
import { notificationsRoute } from "@/routes/_app/notifications";
import { chatRoute } from "@/routes/_app/chat";
import { chatThreadRoute } from "@/routes/_app/chat.$threadId";
import { communityNewRoute } from "@/routes/_app/community.new";
import { communityPostRoute } from "@/routes/_app/community.$postId";
import { communityProductRoute } from "@/routes/_app/community.product.$productId";
import { profileRoute } from "@/routes/_app/profile";
import { debugRoute } from "@/routes/_app/debug";
import { organizationsRoute } from "@/routes/_app/organizations";
import { activityRoute } from "@/routes/_app/activity";
import { trashRoute } from "@/routes/_app/trash";
import { cc21ProbeRoute } from "@/routes/_app/cc21-probe";
import { organizationDetailRoute } from "@/routes/_app/organizations.$orgId";
import { invitationsRoute } from "@/routes/_app/invitations";
import { adminRoute } from "@/routes/_app/admin";
import { adminIndexRoute } from "@/routes/_app/admin.index";
import { adminUsersRoute } from "@/routes/_app/admin.users";
import { adminAuditLogRoute } from "@/routes/_app/admin.audit-log";
import { adminIntegrationsRoute } from "@/routes/_app/admin.integrations";
import { adminFlagsRoute } from "@/routes/_app/admin.flags";
import { adminMetricsRoute } from "@/routes/_app/admin.metrics";
import { adminModerationRoute } from "@/routes/_app/admin.moderation";
import { adminTicketsRoute } from "@/routes/_app/admin.tickets";
import { adminStorageRoute } from "@/routes/_app/admin.storage";
import { adminThemeAuditRoute } from "@/routes/_app/admin.theme-audit";
import { adminThemesRoute } from "@/routes/_app/admin.themes";
import { adminFontsRoute } from "@/routes/_app/admin.fonts";
import { adminEmailRoute } from "@/routes/_app/admin.email";
import { adminEmailCampaignsRoute } from "@/routes/_app/admin.email.campaigns";
import { themeGalleryRoute } from "@/routes/_app/themes";
import { languageRoute } from "@/routes/_app/language";
import { profileFontsRoute } from "@/routes/_app/profile.fonts";
import { profileShortcutsRoute } from "@/routes/_app/profile.shortcuts";
import { profileDeleteAccountRoute } from "@/routes/_app/profile.delete-account";
import { supportRoute } from "@/routes/_app/support";
import { settingsAlertsRoute } from "@/routes/_app/settings.alerts";
import { profileTransferSendRoute } from "@/routes/_app/profile.transfer-send";
import { profileTransferReceiveRoute } from "@/routes/_app/profile.transfer-receive";

const routeTree = rootRoute.addChildren([
  socialCallbackRoute,
  authRoute.addChildren([
    loginRoute,
    registerRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    verifyEmailRoute,
  ]),
  appRoute.addChildren([
    // Fixture de regresión CC-21 (Select/Popover alcanzable dentro de un Dialog
    // modal). Solo en DEV: sirve al e2e cc21-probe.spec y NO se registra en prod.
    ...(import.meta.env.DEV ? [cc21ProbeRoute] : []),
    indexRoute, // path: "/" — Aldea (Cottage home)
    // 🎯 Catálogo ⇄ colección + escáner (Wave 0)
    catalogRoute,
    catalogProductRoute,
    scannerRoute,
    collectionRoute,
    // Tabs de shell rellenados por Wave 1
    marketRoute,
    journalRoute,
    journalPrivacyRoute,
    // Gamificación (Wave 1, agente E): rangos + stickers (tab Journal).
    ranksRoute,
    stickersRoute,
    communityRoute,
    socialFollowersRoute,
    socialFollowingRoute,
    notificationsRoute,
    chatRoute,
    chatThreadRoute,
    communityNewRoute,
    communityPostRoute,
    communityProductRoute,
    activityRoute,
    trashRoute,
    organizationsRoute,
    organizationDetailRoute,
    invitationsRoute,
    adminRoute.addChildren([
      adminIndexRoute,
      adminUsersRoute,
      adminAuditLogRoute,
      adminIntegrationsRoute,
      adminFlagsRoute,
      adminMetricsRoute,
      adminStorageRoute,
      adminThemeAuditRoute,
      adminThemesRoute,
      adminFontsRoute,
      adminEmailRoute,
      adminEmailCampaignsRoute,
      adminModerationRoute,
      adminTicketsRoute,
    ]),
    themeGalleryRoute,
    languageRoute,
    profileRoute,
    profileFontsRoute,
    profileShortcutsRoute,
    profileDeleteAccountRoute,
    supportRoute,
    settingsAlertsRoute,
    profileTransferSendRoute,
    profileTransferReceiveRoute,
    debugRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  // "intent" preloads only when the user expresses intent (hover/focus on a
  // <Link>). The previous "viewport" setting eagerly fired `beforeLoad` and
  // PowerSync `resolveTables` EXPLAIN queries for ~20 sidebar routes the
  // moment the dashboard mounted. Combined with PowerSync's initial-sync
  // batches (each one re-invalidating subscribed queries), this saturated
  // the main thread for several seconds after login. Hover-only is the
  // standard tradeoff: a tiny first-hover latency in exchange for a calm
  // post-login render.
  defaultPreload: "intent",
  // oxlint-disable-next-line eslint(no-magic-numbers)
  defaultPreloadStaleTime: 30_000, // 30 s — don't re-preload the same route within 30 s
  // URL desconocida → estado "página no encontrada" con CTA, en vez de pantalla en blanco.
  defaultNotFoundComponent: NotFoundRoute,
});

declare module "@tanstack/solid-router" {
  interface Register {
    router: typeof router;
  }
}
